/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { IWithTranslationProps } from '@scuf/localization';
import moment from 'moment';
import React from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import { config } from '../constants';
import {
  formatters,
  Formatters as formattersWithLocale
} from '@hcecbp/i18n-utils';
import {
  IArraySeriesProps,
  IPointSeriesProps
} from '../EnergyOptimizationInterfaces';
import {
  shouldRenderBaselineCheck,
  cssExcludeDayCheck,
  cssExcludeDayWithToolTipCheck,
  shouldRenderCheck,
  shouldRenderBCheck,
  symbolObj
} from '../../../shared/Utils/Utils';
import { getStrategyStatusLabel } from '../../../shared/Utils/EnergyOptimizationUtils';
import _ from 'lodash';

const d = descriptors.energyManagement;

export interface IEnergyOptimizationChartTooltipProps
  extends IWithTranslationProps {
  xValue: number;
  actual?: IArraySeriesProps;
  inactive?: IPointSeriesProps;
  eoStatus?: IPointSeriesProps | any;
  excluded?: IPointSeriesProps;
  savings?: IArraySeriesProps;
  siteComfort?: IArraySeriesProps;
  activeHours?: IArraySeriesProps;
  currencyName?: string;
  showComfortData?: boolean;
  isConsumption?: boolean;
  measurementUnit?: string;
  isAllUtility?: boolean;
}

const EnergyOptimizationChartTooltip = ({
  xValue,
  actual = {},
  inactive = {},
  excluded = {},
  savings = {},
  siteComfort = {},
  activeHours = {},
  eoStatus = { data: [] },
  currencyName = config.defaultCurrencyName,
  showComfortData,
  t,
  lng,
  isConsumption,
  measurementUnit,
  isAllUtility
}: IEnergyOptimizationChartTooltipProps) => {
  const shouldRenderEOStatus = eoStatus?.data?.some(
    (point: any) => point.x === xValue
  );
  const EOStatusValue = eoStatus?.data?.find((point: any) => point.x === xValue)
    ?.y;

  const shouldRenderInactiveDay = inactive?.data?.some(
    (point) => point.x === xValue
  );
  const shouldRenderExcludedDay = excluded?.data?.some(
    (point) => point.x === xValue
  );
  const excludedDayDescripton = excluded?.data?.find(
    (point) => point.x === xValue
  )?.description;
  let cssExcludeDay = cssExcludeDayCheck(excludedDayDescripton);
  let cssExcludeDayWithToolTip = cssExcludeDayWithToolTipCheck(
    excludedDayDescripton
  );

  const excludedDayClassName = `excluded-day-text ${cssExcludeDay}`;
  const shouldRenderActual = actual?.data?.some((point) => point[0] === xValue);
  const shouldRenderSavings = savings?.data?.some(
    (point) => point[0] === xValue
  );
  const shouldRenderBaseline = shouldRenderBaselineCheck(
    shouldRenderActual,
    shouldRenderSavings
  );

  const shouldRenderActiveHours = activeHours?.data?.some(
    (point) => point[0] === xValue
  );

  let savingsTooltip = config.noDataSymbol;
  if (shouldRenderSavings) {
    savingsTooltip = isConsumption
      ? _.toString(savings.data!.find((point) => point[0] === xValue)![1])
      : formatters.number.currency(currencyName)(
          savings.data!.find((point) => point[0] === xValue)![1]
        );
  }

  const savingsData =
    !shouldRenderInactiveDay &&
    !shouldRenderExcludedDay &&
    generateTooltipSeriesRow(
      t(d.SAVINGS),
      savingsTooltip,
      'savings',
      measurementUnit,
      isAllUtility
    );

  const renderEOStatus = () => {
    return (
      EOStatusValue &&
      Object.keys(EOStatusValue).map((key) => {
        const statusKey = Number(key);
        const label = getStrategyStatusLabel(t, statusKey);
        const hours = EOStatusValue[statusKey];
        if (!label || hours === undefined) {
          return null;
        }
        return (
          <div className="eo-status-div" key={`${statusKey}_${hours}`}>
            <span>{label}:</span>
            <span className="hour-value">
              {!isNaN(Number(hours)) && Number(hours).toFixed(1)} hr
            </span>
          </div>
        );
      })
    );
  };

  return (
    <div className="energy-optimization-chart-tooltip-container">
      <div className="tooltip-row tooltip-title">
        {formattersWithLocale(lng).datetime.yearMonthDay(
          moment(xValue).toDate()
        )}
      </div>
      {shouldRenderInactiveDay ? (
        <div className="tooltip-row">
          <div className="tooltip-col">
            <div className="tooltip-symbol inactive-days-symbol" />
          </div>
          <div className="tooltip-col tooltip-series-text-container">
            <div>{t(d.inactiveDay)}</div>
          </div>
        </div>
      ) : null}
      {shouldRenderExcludedDay ? (
        <div className={`tooltip-row${cssExcludeDayWithToolTip}`}>
          <div className="tooltip-col">
            <div className="tooltip-symbol excluded-days-symbol" />
          </div>
          <div className="tooltip-col tooltip-series-text-container">
            <div>{t(d.excludedDay)}</div>
          </div>
        </div>
      ) : null}
      {excludedDayDescripton ? (
        <div className="tooltip-row excluded-day-description-tooltip-row">
          <span className={excludedDayClassName}>{excludedDayDescripton}</span>
        </div>
      ) : null}
      {shouldRenderActiveHours &&
      (!EOStatusValue || Object.keys(EOStatusValue)?.length === 0) &&
      !shouldRenderInactiveDay &&
      !shouldRenderExcludedDay
        ? generateTooltipSeriesRow(
            t(d.activeHours),
            formatters.number.unit('hour')(
              Math.round(
                activeHours.data!.find((point) => point[0] === xValue)![1]
              )
            ),
            '',
            undefined,
            isAllUtility
          )
        : null}
      {eoStatus?.data.length &&
      EOStatusValue && Object.keys(EOStatusValue)?.length > 0 &&
      !shouldRenderInactiveDay &&
      !shouldRenderExcludedDay
        ? generateTooltipSeriesRow(
            t(d.activeDay),
            '',
            '',
            undefined,
            isAllUtility
          )
        : null}
      {shouldRenderEOStatus ? (
        <div className="tooltip-row">
          <div className="tooltip-col tooltip-series-text-container">
            {renderEOStatus()}
          </div>
        </div>
      ) : null}
      {savingsData}
      {generateTooltipSeriesRow(
        t(d.ACTUAL),
        shouldRenderCheck(
          shouldRenderActual,
          currencyName,
          actual,
          xValue,
          isConsumption
        ),
        'actual',
        measurementUnit,
        isAllUtility
      )}
      {generateTooltipSeriesRow(
        t(d.CALCULATED),
        shouldRenderBCheck(
          shouldRenderBaseline,
          currencyName,
          savings,
          actual,
          xValue,
          isConsumption
        ),
        'baseline',
        measurementUnit,
        isAllUtility
      )}
    </div>
  );
};

export default EnergyOptimizationChartTooltip;

const generateTooltipSeriesRow = (
  title: string,
  value: string,
  classValue?: string,
  unit?: string,
  isAllUtility?: boolean
) => {
  const modifiedValue = value;
  if (
    symbolObj[
      modifiedValue
        .split('')
        .splice(0, 3)
        .join('')
    ]
  ) {
    return (
      <div className="tooltip-row">
        <div className="tooltip-col tooltip-rectangular-symbol-container">
          <div className={classValue} />
        </div>
        <div className="tooltip-col tooltip-series-text-container">
          <div>{title}:</div>
        </div>
        <div className="tooltip-col tooltip-series-value-container">
          <div>{`${
            symbolObj[
              modifiedValue
                .split('')
                .splice(0, 3)
                .join('')
            ]
          } ${value
            .split('')
            .splice(3)
            .join('')}${!isAllUtility && unit ? ' ' + unit : ''}`}</div>
        </div>
      </div>
    );
  } else if (
    symbolObj[
      modifiedValue
        .split('')
        .splice(0, 2)
        .join('')
    ]
  ) {
    return (
      <div className="tooltip-row">
        <div className="tooltip-col tooltip-rectangular-symbol-container">
          <div className={classValue} />
        </div>
        <div className="tooltip-col tooltip-series-text-container">
          <div>{title}:</div>
        </div>
        <div className="tooltip-col tooltip-series-value-container">
          <div>{`${
            symbolObj[
              modifiedValue
                .split('')
                .splice(0, 2)
                .join('')
            ]
          } ${value
            .split('')
            .splice(2)
            .join('')}${!isAllUtility && unit ? ' ' + unit : ''}`}</div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="tooltip-row">
        <div className="tooltip-col tooltip-rectangular-symbol-container">
          <div className={classValue} />
        </div>
        <div className="tooltip-col tooltip-series-text-container">
          <div>{title}:</div>
        </div>
        <div className="tooltip-col tooltip-series-value-container">
          <div>{`${value}${!isAllUtility && unit ? ' ' + unit : ''}`}</div>
        </div>
      </div>
    );
  }
  console.log('modifiedValue', modifiedValue);
};
